import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { Attrs, Node } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { isNodeSelection } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ReplaceWhiteboardEmbedProps } from '../malleableUiPluginType';
import {
	ActionType,
	MauiEmbedFinalizeStatus,
	mauiDiagramPastePluginKey,
} from '../pm-plugins/maui-diagram-paste-plugin';

// The native-embeds auto-converter wraps a MAUI whiteboard URL
// (`...?isRemixDiagram=true`) into an extension node keyed by the
// `maui-whiteboard` experience id, not the generic `whiteboard` experience.
// See native-embeds-editor-extension `createManifest` autoConvert.
const NATIVE_WHITEBOARD_EXTENSION_KEY = 'native-embed:maui-whiteboard';

type WhiteboardEmbedNodeKind = 'embedCard' | 'nativeWhiteboard';

type WhiteboardEmbedNodeInfo = {
	kind: WhiteboardEmbedNodeKind;
	localId?: string;
	url?: string;
};

const getString = (value: unknown): string | undefined =>
	typeof value === 'string' ? value : undefined;

// The native-embeds plugin can persist a whiteboard extension in one of two
// shapes depending on how it was created/transformed:
// - Nested macro shape: `attrs.parameters.macroParams.url.value`
// - Flat shape:         `attrs.parameters.url`
const getNativeWhiteboardUrl = (attrs: Attrs): string | undefined =>
	getString(attrs.parameters?.macroParams?.url?.value) ?? getString(attrs.parameters?.url);

const getWhiteboardContentId = (url: string | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}

	const match = /\/whiteboard\/([^/?#]+)/.exec(url);
	const encodedContentId = match?.[1];
	if (!encodedContentId) {
		return undefined;
	}

	try {
		return decodeURIComponent(encodedContentId);
	} catch {
		return encodedContentId;
	}
};

const getWhiteboardEmbedNodeInfo = (node: Node): WhiteboardEmbedNodeInfo | undefined => {
	if (node.type.name === 'embedCard') {
		return {
			kind: 'embedCard',
			localId: getString(node.attrs.localId),
			url: getString(node.attrs.url),
		};
	}

	if (
		node.type.name === 'extension' &&
		node.attrs.extensionKey === NATIVE_WHITEBOARD_EXTENSION_KEY
	) {
		return {
			kind: 'nativeWhiteboard',
			localId: getString(node.attrs.localId),
			url: getNativeWhiteboardUrl(node.attrs),
		};
	}

	return undefined;
};

const isMatchingWhiteboardContent = (url: string | undefined, contentId: string): boolean =>
	getWhiteboardContentId(url) === contentId;

const isMatchingLocalId = (actualLocalId: string | undefined, expectedLocalId?: string): boolean =>
	expectedLocalId ? actualLocalId === expectedLocalId : true;

export const isWhiteboardEmbedNode = (node: Node, contentId: string, localId?: string): boolean => {
	const info = getWhiteboardEmbedNodeInfo(node);
	if (!info || !isMatchingWhiteboardContent(info.url, contentId)) {
		return false;
	}

	// Finalize can run during the timing window before or after native-embeds
	// converts Rovo's early embedCard into a maui-whiteboard extension. The
	// embedCard has localId; the converted extension does not, so match it by
	// exact contentId.
	if (info.kind === 'nativeWhiteboard') {
		return true;
	}

	return isMatchingLocalId(info.localId, localId);
};

// Position of the remix whiteboard with this contentId, so the ready-signal UI can target the
// board itself (not the thinking-widget anchor). Matches while streaming and after finalize.
export const findWhiteboardNodePosByContentId = (
	doc: Node,
	contentId: string,
): number | undefined => {
	let found: number | undefined;
	doc.descendants((node, pos) => {
		if (found !== undefined) {
			return false;
		}
		if (isWhiteboardEmbedNode(node, contentId)) {
			found = pos;
			return false;
		}
		return true;
	});
	return found;
};

// Only updates the shapes that actually exist on the node, to avoid inventing
// missing nested objects on a flat node (mirrors `getNativeWhiteboardUrl`).
const updateNativeWhiteboardUrlAttrs = (attrs: Attrs, finalizedUrl: string): Attrs => ({
	...attrs,
	parameters: {
		...attrs.parameters,
		...(typeof attrs.parameters?.url === 'string' ? { url: finalizedUrl } : {}),
		...(attrs.parameters?.macroParams
			? {
					macroParams: {
						...attrs.parameters.macroParams,
						url: {
							...attrs.parameters.macroParams.url,
							value: finalizedUrl,
						},
					},
				}
			: {}),
		...(attrs.parameters?.macroMetadata
			? {
					macroMetadata: {
						...attrs.parameters.macroMetadata,
						url: finalizedUrl,
					},
				}
			: {}),
	},
});

export const getWhiteboardUpdateAttrs = (node: Node, finalizedUrl: string): Attrs => {
	const info = getWhiteboardEmbedNodeInfo(node);

	if (info?.kind === 'nativeWhiteboard') {
		return updateNativeWhiteboardUrlAttrs(node.attrs, finalizedUrl);
	}

	return {
		...node.attrs,
		url: finalizedUrl,
	};
};

const updateWhiteboardNodeUrl = (
	tr: Parameters<EditorCommand>[0]['tr'],
	node: Node,
	pos: number,
	contentId: string,
	finalizedUrl: string,
) => {
	tr.setNodeMarkup(pos, undefined, getWhiteboardUpdateAttrs(node, finalizedUrl));
	// Tell the maui-diagram paste plugin that this content has been finalized so
	// the `MauiNativeEmbedBanner` stops surfacing the "needs finalize" warning
	// even if the BE move hasn't propagated by the time the probe fires.
	tr.setMeta(mauiDiagramPastePluginKey, {
		type: ActionType.EMBED_FINALIZE_STATUS_CHANGED,
		contentId,
		status: MauiEmbedFinalizeStatus.FINALIZED,
	});
	if (fg('platform_remix_fix_node_replacement_selection')) {
		tr.setSelection(NodeSelection.create(tr.doc, pos));
	}
	// Finalize's setNodeMarkup would otherwise trigger the editor's scroll-into-view plugin. Suppress
	// it so the ready-signal owns the scroll-vs-"View"-bar decision instead of a forced page scroll.
	tr.setMeta('scrollIntoView', false);
	return tr;
};

const findWhiteboardNode = (
	doc: Parameters<EditorCommand>[0]['tr']['doc'],
	contentId: string,
	localId?: string,
):
	| { node: Node; pos: number }
	| { outcome: 'content-id-mismatch' | 'local-id-mismatch' | 'node-not-found' } => {
	let nodeResult: { node: Node; pos: number } | undefined;
	let hasMatchingContentId = false;
	let hasMatchingLocalId = false;

	doc.descendants((node, pos) => {
		if (nodeResult) {
			return false;
		}
		if (isWhiteboardEmbedNode(node, contentId, localId)) {
			nodeResult = { node, pos };
			return false;
		}
		const info = getWhiteboardEmbedNodeInfo(node);
		if (!info) {
			return true;
		}

		hasMatchingContentId ||= isMatchingWhiteboardContent(info.url, contentId);
		hasMatchingLocalId ||= Boolean(localId && info.localId === localId);
		return !(hasMatchingContentId && hasMatchingLocalId);
	});

	if (nodeResult) {
		return nodeResult;
	}
	if (hasMatchingContentId) {
		return { outcome: 'local-id-mismatch' };
	}
	if (hasMatchingLocalId) {
		return { outcome: 'content-id-mismatch' };
	}
	return { outcome: 'node-not-found' };
};

/**
 * Replaces the temporary streaming whiteboard URL with the finalized canonical URL.
 *
 * The early insertion starts as an embedCard, but the native-embeds plugin can transform
 * it into a native whiteboard extension before finalization completes. We therefore
 * match embedCards by localId/contentId and converted extensions by exact contentId,
 * instead of relying on the user's current selection or on one specific node type.
 */
export const replaceWhiteboardEmbed =
	({ contentId, finalizedUrl, localId, onOutcome }: ReplaceWhiteboardEmbedProps): EditorCommand =>
	({ tr }) => {
		// Fast path: if the target is already selected, avoid walking the whole document.
		if (
			isNodeSelection(tr.selection) &&
			isWhiteboardEmbedNode(tr.selection.node, contentId, localId)
		) {
			onOutcome?.('replaced');
			return updateWhiteboardNodeUrl(
				tr,
				tr.selection.node,
				tr.selection.$anchor.pos,
				contentId,
				finalizedUrl,
			);
		}

		const nodeResult = findWhiteboardNode(tr.doc, contentId, localId);
		if ('node' in nodeResult) {
			onOutcome?.('replaced');
			return updateWhiteboardNodeUrl(tr, nodeResult.node, nodeResult.pos, contentId, finalizedUrl);
		}

		onOutcome?.(nodeResult.outcome);
		return null;
	};
